import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Search,
  Bookmark,
  BookmarkCheck,
  ExternalLink,
  Loader2,
  Filter,
  RefreshCw,
  FileText,
  Scale,
  Coins,
  BookOpen,
  ListChecks,
  ChevronRight,
  X,
  Calendar,
  Building2,
  StickyNote,
  Check,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';

// Article type icons
const TYPE_ICONS = {
  'wetswijziging-nl': Scale,
  'regel-nl': FileText,
  'subsidie-nl': Coins,
  'artikel-nl': BookOpen,
  'stepbystep': ListChecks,
};

// Article type colors (Tailwind classes)
const TYPE_STYLES = {
  'wetswijziging-nl': { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300', border: 'border-amber-200 dark:border-amber-800' },
  'regel-nl': { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300', border: 'border-blue-200 dark:border-blue-800' },
  'subsidie-nl': { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300', border: 'border-green-200 dark:border-green-800' },
  'artikel-nl': { bg: 'bg-neutral-100 dark:bg-neutral-800', text: 'text-neutral-700 dark:text-neutral-300', border: 'border-neutral-200 dark:border-neutral-700' },
  'stepbystep': { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-300', border: 'border-purple-200 dark:border-purple-800' },
};

const TYPE_LABELS = {
  'wetswijziging-nl': 'Wetswijziging',
  'regel-nl': 'Regel',
  'subsidie-nl': 'Subsidie',
  'artikel-nl': 'Artikel',
  'stepbystep': 'Stappenplan',
};

/**
 * RegulationsFeed - Component to display and manage regulations from Ondernemersplein
 * Used in the "Wet & Regelgeving" tab of the Strategie page
 */
const RegulationsFeed = () => {
  const { api } = useAuth();
  const [activeTab, setActiveTab] = useState('feed');
  const [loading, setLoading] = useState(true);
  const [articles, setArticles] = useState([]);
  const [savedArticles, setSavedArticles] = useState([]);
  const [groupedCounts, setGroupedCounts] = useState({});
  const [pagination, setPagination] = useState({ total: 0, offset: 0, limit: 20 });
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  
  // Modal state
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // Fetch articles from API
  const fetchArticles = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        offset: pagination.offset.toString(),
        limit: pagination.limit.toString(),
      });
      
      if (selectedType !== 'all') {
        params.append('article_type', selectedType);
      }
      if (searchQuery) {
        params.append('search', searchQuery);
      }
      
      const response = await api.get(`/regulations/articles?${params}`);
      setArticles(response.data.articles || []);
      setGroupedCounts(response.data.grouped_counts || {});
      setPagination(prev => ({
        ...prev,
        total: response.data.total || 0
      }));
    } catch (error) {
      console.error('Error fetching articles:', error);
      toast.error('Kon artikelen niet laden');
    } finally {
      setLoading(false);
    }
  }, [api, pagination.offset, pagination.limit, selectedType, searchQuery]);

  // Fetch saved articles
  const fetchSavedArticles = useCallback(async () => {
    try {
      const response = await api.get('/regulations/saved');
      setSavedArticles(response.data.saved_articles || []);
    } catch (error) {
      console.error('Error fetching saved articles:', error);
    }
  }, [api]);

  useEffect(() => {
    fetchArticles();
    fetchSavedArticles();
  }, [fetchArticles, fetchSavedArticles]);

  // Save/unsave article
  const handleSaveArticle = async (article) => {
    try {
      if (article.is_saved) {
        // Remove from saved
        await api.delete(`/regulations/saved/${article.identifier}`);
        toast.success('Artikel verwijderd uit opgeslagen');
      } else {
        // Save article
        await api.post('/regulations/save', {
          article_id: article.identifier,
          headline: article.headline,
          article_type: article.article_type,
          url: article.url,
          date_modified: article.date_modified,
        });
        toast.success('Artikel opgeslagen');
      }
      
      // Refresh both lists
      fetchArticles();
      fetchSavedArticles();
    } catch (error) {
      console.error('Error saving article:', error);
      toast.error('Kon artikel niet opslaan');
    }
  };

  // Open note modal
  const handleOpenNoteModal = (article) => {
    setSelectedArticle(article);
    setNoteText(article.notes || '');
    setShowNoteModal(true);
  };

  // Save note
  const handleSaveNote = async () => {
    if (!selectedArticle) return;
    
    try {
      setSavingNote(true);
      
      // If not already saved, save first
      if (!selectedArticle.is_saved) {
        await api.post('/regulations/save', {
          article_id: selectedArticle.identifier,
          headline: selectedArticle.headline,
          article_type: selectedArticle.article_type,
          url: selectedArticle.url,
          date_modified: selectedArticle.date_modified,
          notes: noteText,
        });
      } else {
        // Update notes
        await api.patch(`/regulations/saved/${selectedArticle.identifier}`, {
          notes: noteText
        });
      }
      
      toast.success('Notitie opgeslagen');
      setShowNoteModal(false);
      fetchArticles();
      fetchSavedArticles();
    } catch (error) {
      console.error('Error saving note:', error);
      toast.error('Kon notitie niet opslaan');
    } finally {
      setSavingNote(false);
    }
  };

  // Delete saved article
  const handleDeleteSaved = async (articleId) => {
    try {
      await api.delete(`/regulations/saved/${articleId}`);
      toast.success('Artikel verwijderd');
      fetchSavedArticles();
      fetchArticles();
    } catch (error) {
      console.error('Error deleting article:', error);
      toast.error('Kon artikel niet verwijderen');
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-NL', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  // Article card component
  const ArticleCard = ({ article, showActions = true }) => {
    const Icon = TYPE_ICONS[article.article_type] || FileText;
    const styles = TYPE_STYLES[article.article_type] || TYPE_STYLES['artikel-nl'];
    const label = TYPE_LABELS[article.article_type] || 'Artikel';
    
    return (
      <div 
        className={`p-4 rounded-lg border ${styles.border} ${article.is_saved ? 'bg-blue-50/50 dark:bg-blue-900/10' : 'bg-white dark:bg-neutral-900'} hover:shadow-md transition-all`}
        data-testid={`article-${article.identifier}`}
      >
        <div className="flex items-start gap-3">
          {/* Type Icon */}
          <div className={`h-10 w-10 rounded-lg ${styles.bg} flex items-center justify-center flex-shrink-0`}>
            <Icon className={`h-5 w-5 ${styles.text}`} strokeWidth={1.5} />
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Badge className={`${styles.bg} ${styles.text} text-xs`}>
                {label}
              </Badge>
              {article.date_modified && (
                <span className="text-xs text-neutral-500 flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {formatDate(article.date_modified)}
                </span>
              )}
            </div>
            
            <h4 className="text-sm font-medium text-black dark:text-white line-clamp-2 mb-2">
              {article.headline}
            </h4>
            
            {article.notes && (
              <div className="flex items-start gap-1.5 p-2 rounded bg-amber-50 dark:bg-amber-900/20 mb-2">
                <StickyNote className="h-3.5 w-3.5 text-amber-600 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <p className="text-xs text-amber-700 dark:text-amber-300 line-clamp-2">
                  {article.notes}
                </p>
              </div>
            )}
            
            {/* Actions */}
            {showActions && (
              <div className="flex items-center gap-2">
                <a 
                  href={article.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                >
                  Lees meer
                  <ExternalLink className="h-3 w-3" />
                </a>
                
                <button
                  onClick={() => handleSaveArticle(article)}
                  className={`text-xs flex items-center gap-1 ${
                    article.is_saved 
                      ? 'text-blue-600 hover:text-blue-700' 
                      : 'text-neutral-500 hover:text-neutral-700'
                  }`}
                >
                  {article.is_saved ? (
                    <>
                      <BookmarkCheck className="h-3.5 w-3.5" strokeWidth={1.5} />
                      Opgeslagen
                    </>
                  ) : (
                    <>
                      <Bookmark className="h-3.5 w-3.5" strokeWidth={1.5} />
                      Opslaan
                    </>
                  )}
                </button>
                
                <button
                  onClick={() => handleOpenNoteModal(article)}
                  className="text-xs text-neutral-500 hover:text-neutral-700 flex items-center gap-1"
                >
                  <StickyNote className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {article.notes ? 'Bewerk notitie' : 'Notitie'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header with Tabs */}
      <div className="flex items-center justify-between">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="flex items-center justify-between mb-4">
            <TabsList>
              <TabsTrigger value="feed" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Overheidsfeed
              </TabsTrigger>
              <TabsTrigger value="saved" className="flex items-center gap-2">
                <BookmarkCheck className="h-4 w-4" />
                Opgeslagen ({savedArticles.length})
              </TabsTrigger>
            </TabsList>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                fetchArticles();
                fetchSavedArticles();
              }}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Vernieuwen
            </Button>
          </div>

          {/* Feed Tab */}
          <TabsContent value="feed" className="mt-0 space-y-4">
            {/* Filters */}
            <Card className="border-neutral-200 dark:border-neutral-800">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row gap-3">
                  {/* Search */}
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
                    <Input
                      placeholder="Zoek op titel..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9"
                      data-testid="search-regulations"
                    />
                  </div>
                  
                  {/* Type Filter */}
                  <Select value={selectedType} onValueChange={setSelectedType}>
                    <SelectTrigger className="w-full sm:w-[180px]" data-testid="filter-type">
                      <Filter className="h-4 w-4 mr-2 text-neutral-400" />
                      <SelectValue placeholder="Alle types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle types</SelectItem>
                      <SelectItem value="wetswijziging-nl">
                        Wetswijzigingen ({groupedCounts['wetswijziging-nl'] || 0})
                      </SelectItem>
                      <SelectItem value="regel-nl">
                        Regels ({groupedCounts['regel-nl'] || 0})
                      </SelectItem>
                      <SelectItem value="subsidie-nl">
                        Subsidies ({groupedCounts['subsidie-nl'] || 0})
                      </SelectItem>
                      <SelectItem value="artikel-nl">
                        Artikelen ({groupedCounts['artikel-nl'] || 0})
                      </SelectItem>
                      <SelectItem value="stepbystep">
                        Stappenplannen ({groupedCounts['stepbystep'] || 0})
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Type Counts */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {Object.entries(TYPE_LABELS).map(([type, label]) => {
                const Icon = TYPE_ICONS[type];
                const styles = TYPE_STYLES[type];
                const count = groupedCounts[type] || 0;
                const isActive = selectedType === type;
                
                return (
                  <button
                    key={type}
                    onClick={() => setSelectedType(isActive ? 'all' : type)}
                    className={`p-3 rounded-lg border transition-all ${
                      isActive 
                        ? `${styles.border} ${styles.bg}` 
                        : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon className={`h-4 w-4 ${styles.text}`} strokeWidth={1.5} />
                      <span className={`text-xs font-medium ${isActive ? styles.text : 'text-neutral-600 dark:text-neutral-400'}`}>
                        {label}
                      </span>
                    </div>
                    <p className={`text-lg font-bold mt-1 ${isActive ? styles.text : 'text-black dark:text-white'}`}>
                      {count}
                    </p>
                  </button>
                );
              })}
            </div>

            {/* Articles List */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-neutral-400" />
              </div>
            ) : articles.length === 0 ? (
              <Card className="border-neutral-200 dark:border-neutral-800">
                <CardContent className="py-12 text-center">
                  <FileText className="h-12 w-12 mx-auto mb-4 text-neutral-300" strokeWidth={1.5} />
                  <p className="text-neutral-500">Geen artikelen gevonden</p>
                  <p className="text-xs text-neutral-400 mt-1">
                    Pas uw zoekopdracht of filters aan
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {articles.map((article) => (
                  <ArticleCard key={article.identifier} article={article} />
                ))}
              </div>
            )}

            {/* Pagination info */}
            {pagination.total > 0 && (
              <div className="flex items-center justify-between text-sm text-neutral-500">
                <span>
                  {articles.length} van {pagination.total} artikelen
                </span>
                <a 
                  href="https://opendata.ondernemersplein.nl" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                >
                  Bron: Ondernemersplein.nl
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            )}
          </TabsContent>

          {/* Saved Tab */}
          <TabsContent value="saved" className="mt-0 space-y-4">
            {savedArticles.length === 0 ? (
              <Card className="border-neutral-200 dark:border-neutral-800">
                <CardContent className="py-12 text-center">
                  <BookmarkCheck className="h-12 w-12 mx-auto mb-4 text-neutral-300" strokeWidth={1.5} />
                  <p className="text-neutral-500">Nog geen artikelen opgeslagen</p>
                  <p className="text-xs text-neutral-400 mt-1">
                    Sla relevante wet- en regelgeving op voor uw organisatie
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {savedArticles.map((article) => (
                  <div 
                    key={article.article_id}
                    className="p-4 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10"
                  >
                    <div className="flex items-start gap-3">
                      {/* Type Icon */}
                      <div className={`h-10 w-10 rounded-lg ${TYPE_STYLES[article.article_type]?.bg || 'bg-neutral-100'} flex items-center justify-center flex-shrink-0`}>
                        {(() => {
                          const Icon = TYPE_ICONS[article.article_type] || FileText;
                          const styles = TYPE_STYLES[article.article_type] || TYPE_STYLES['artikel-nl'];
                          return <Icon className={`h-5 w-5 ${styles.text}`} strokeWidth={1.5} />;
                        })()}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={`${TYPE_STYLES[article.article_type]?.bg || 'bg-neutral-100'} ${TYPE_STYLES[article.article_type]?.text || 'text-neutral-700'} text-xs`}>
                            {TYPE_LABELS[article.article_type] || 'Artikel'}
                          </Badge>
                          {article.date_modified && (
                            <span className="text-xs text-neutral-500 flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(article.date_modified)}
                            </span>
                          )}
                        </div>
                        
                        <h4 className="text-sm font-medium text-black dark:text-white mb-2">
                          {article.headline}
                        </h4>
                        
                        {article.notes && (
                          <div className="flex items-start gap-1.5 p-2 rounded bg-amber-50 dark:bg-amber-900/20 mb-2">
                            <StickyNote className="h-3.5 w-3.5 text-amber-600 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                            <p className="text-xs text-amber-700 dark:text-amber-300">
                              {article.notes}
                            </p>
                          </div>
                        )}
                        
                        {/* Actions */}
                        <div className="flex items-center gap-3">
                          <a 
                            href={article.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                          >
                            Lees meer
                            <ExternalLink className="h-3 w-3" />
                          </a>
                          
                          <button
                            onClick={() => handleOpenNoteModal({
                              identifier: article.article_id,
                              headline: article.headline,
                              article_type: article.article_type,
                              url: article.url,
                              date_modified: article.date_modified,
                              notes: article.notes,
                              is_saved: true
                            })}
                            className="text-xs text-neutral-500 hover:text-neutral-700 flex items-center gap-1"
                          >
                            <StickyNote className="h-3.5 w-3.5" strokeWidth={1.5} />
                            {article.notes ? 'Bewerk notitie' : 'Notitie toevoegen'}
                          </button>
                          
                          <button
                            onClick={() => handleDeleteSaved(article.article_id)}
                            className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                          >
                            <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                            Verwijderen
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Note Modal */}
      <Dialog open={showNoteModal} onOpenChange={setShowNoteModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Notitie toevoegen</DialogTitle>
            <DialogDescription>
              Voeg een notitie toe om te onthouden waarom dit artikel relevant is voor uw organisatie.
            </DialogDescription>
          </DialogHeader>
          
          {selectedArticle && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-neutral-50 dark:bg-neutral-800">
                <p className="text-sm font-medium text-black dark:text-white line-clamp-2">
                  {selectedArticle.headline}
                </p>
              </div>
              
              <Textarea
                placeholder="Bijv. 'Relevant voor onze GDPR compliance' of 'Actie nodig voor Q2'"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                rows={4}
                data-testid="note-textarea"
              />
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNoteModal(false)}>
              Annuleren
            </Button>
            <Button 
              onClick={handleSaveNote} 
              disabled={savingNote}
              className="bg-black text-white hover:bg-neutral-800"
            >
              {savingNote ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Opslaan...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Opslaan
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RegulationsFeed;
